/**
 * Chord — a stack of pitches built from a root by stacking intervals.
 *
 * Chords are represented as (root, intervals from root, optional inversion).
 * The engine supports triads, sevenths, extensions (9, 11, 13), and altered chords.
 *
 * Quality detection (notes → chord name) is also provided — useful for
 * analyzing user input or arbitrary pitch collections.
 */

import { type Pitch, toMidi, pitchClass } from "./pitch.js";
import { type Interval, parseInterval, transposeByInterval, intervalToString } from "./interval.js";

export interface Chord {
  root: Pitch;
  intervals: Interval[]; // From root. Includes P1 at index 0.
  symbol: string;        // "Cmaj7", "F#m7b5", etc.
  /** Inversion: 0 = root position, 1 = first inversion, 2 = second, etc. */
  inversion: number;
}

export type ChordQuality =
  // Triads
  | "maj" | "min" | "dim" | "aug" | "sus2" | "sus4"
  // Seventh chords
  | "maj7" | "min7" | "dom7" | "min7b5" | "dim7" | "minMaj7" | "augMaj7" | "aug7"
  // Sixth chords
  | "6" | "min6"
  // Extended chords (the engine supports 9/11/13 by stacking diatonic thirds)
  | "maj9" | "min9" | "dom9"
  | "maj11" | "min11" | "dom11"
  | "maj13" | "min13" | "dom13"
  // Altered dominants (jazz harmony — V7 with chromatic upper-extension alterations)
  | "dom7b9" | "dom7s9" | "dom7s11" | "dom7b13";

const CHORD_PATTERNS: Record<ChordQuality, string[]> = {
  // Triads
  "maj":     ["P1", "M3", "P5"],
  "min":     ["P1", "m3", "P5"],
  "dim":     ["P1", "m3", "d5"],
  "aug":     ["P1", "M3", "A5"],
  "sus2":    ["P1", "M2", "P5"],
  "sus4":    ["P1", "P4", "P5"],
  // Sevenths
  "maj7":    ["P1", "M3", "P5", "M7"],
  "min7":    ["P1", "m3", "P5", "m7"],
  "dom7":    ["P1", "M3", "P5", "m7"],
  "min7b5":  ["P1", "m3", "d5", "m7"],
  "dim7":    ["P1", "m3", "d5", "d7"],
  "minMaj7": ["P1", "m3", "P5", "M7"],
  "augMaj7": ["P1", "M3", "A5", "M7"],
  "aug7":    ["P1", "M3", "A5", "m7"],
  // Sixths
  "6":       ["P1", "M3", "P5", "M6"],
  "min6":    ["P1", "m3", "P5", "M6"],
  // Extensions (continue stacking thirds above the 7th)
  "maj9":    ["P1", "M3", "P5", "M7", "M9"],
  "min9":    ["P1", "m3", "P5", "m7", "M9"],
  "dom9":    ["P1", "M3", "P5", "m7", "M9"],
  "maj11":   ["P1", "M3", "P5", "M7", "M9", "P11"],
  "min11":   ["P1", "m3", "P5", "m7", "M9", "P11"],
  "dom11":   ["P1", "M3", "P5", "m7", "M9", "P11"],
  "maj13":   ["P1", "M3", "P5", "M7", "M9", "P11", "M13"],
  "min13":   ["P1", "m3", "P5", "m7", "M9", "P11", "M13"],
  "dom13":   ["P1", "M3", "P5", "m7", "M9", "P11", "M13"],
  // Altered dominants — V7 with chromatic alterations on the 9, 11, or 13.
  // Jazz convention: "alt" implies the dominant has at least one altered tension.
  "dom7b9":  ["P1", "M3", "P5", "m7", "m9"],
  "dom7s9":  ["P1", "M3", "P5", "m7", "A9"],
  "dom7s11": ["P1", "M3", "P5", "m7", "M9", "A11"],
  "dom7b13": ["P1", "M3", "P5", "m7", "M9", "P11", "m13"],
};

// Special handling: parseInterval doesn't natively know "d7" but we need it for dim7.
// Compute it explicitly.
function getInterval(s: string): Interval {
  if (s === "d7") return { number: 7, semitones: 9 }; // diminished 7th = 9 semitones
  const iv = parseInterval(s);
  if (!iv) throw new Error(`Invalid interval string: ${s}`);
  return iv;
}

const SYMBOL_NAMES: Record<ChordQuality, string> = {
  "maj": "", "min": "m", "dim": "°", "aug": "+", "sus2": "sus2", "sus4": "sus4",
  "maj7": "maj7", "min7": "m7", "dom7": "7", "min7b5": "m7♭5", "dim7": "°7",
  "minMaj7": "mMaj7", "augMaj7": "+Maj7", "aug7": "+7",
  "6": "6", "min6": "m6",
  "maj9": "maj9", "min9": "m9", "dom9": "9",
  "maj11": "maj11", "min11": "m11", "dom11": "11",
  "maj13": "maj13", "min13": "m13", "dom13": "13",
  "dom7b9": "7♭9", "dom7s9": "7♯9", "dom7s11": "7♯11", "dom7b13": "7♭13",
};

import { pitchToString } from "./pitch.js";

/** Build a chord from a root and quality. */
export function chord(root: Pitch, quality: ChordQuality, inversion = 0): Chord {
  const pattern = CHORD_PATTERNS[quality];
  const intervals = pattern.map(getInterval);
  if (inversion < 0 || inversion >= intervals.length) {
    throw new Error(`Inversion ${inversion} out of range for ${quality} chord`);
  }
  // Root letter only (no octave) for symbol display.
  const rootName = pitchToString(root).replace(/-?\d+$/, "");
  const symbol = `${rootName}${SYMBOL_NAMES[quality]}`;
  return { root, intervals, symbol, inversion };
}

/** Get the pitches of a chord in close-position root order (no inversion applied). */
export function chordPitches(c: Chord): Pitch[] {
  return c.intervals.map((iv) => transposeByInterval(c.root, iv));
}

/**
 * Get the pitches in voicing order, applying inversion.
 * For inversion N, the bottom N notes are moved up an octave.
 */
export function chordVoicing(c: Chord): Pitch[] {
  const pitches = chordPitches(c);
  if (c.inversion === 0) return pitches;
  // Move the bottom `inversion` notes up an octave.
  const reordered: Pitch[] = [
    ...pitches.slice(c.inversion),
    ...pitches.slice(0, c.inversion).map((p) => ({ ...p, octave: p.octave + 1 })),
  ];
  return reordered;
}

/** Bass note (lowest pitch) of a chord, accounting for inversion. */
export function chordBass(c: Chord): Pitch {
  return chordVoicing(c)[0]!;
}

/**
 * Detect chord quality from a set of notes.
 * Returns the most likely (root, quality) pair, or null if no match.
 *
 * Algorithm: try each note as the root; compute the pitch-class set relative to that root;
 * compare against known patterns. The match with the highest interval-count overlap wins.
 */
export function detectChord(pitches: Pitch[]): { root: Pitch; quality: ChordQuality } | null {
  if (pitches.length < 2) return null;

  // Sort by MIDI for consistent processing.
  const sorted = [...pitches].sort((a, b) => toMidi(a) - toMidi(b));
  const pitchClasses = new Set(sorted.map(pitchClass));

  let bestMatch: { root: Pitch; quality: ChordQuality; score: number } | null = null;

  for (const candidateRoot of sorted) {
    const rootPc = pitchClass(candidateRoot);
    // Compute the set of intervals (in semitones, 0-11) from root.
    const intervalsFromRoot = new Set<number>();
    for (const pc of pitchClasses) {
      intervalsFromRoot.add(((pc - rootPc) + 12) % 12);
    }

    for (const [quality, pattern] of Object.entries(CHORD_PATTERNS) as Array<[ChordQuality, string[]]>) {
      const expectedSemis = new Set(pattern.map((s) => getInterval(s).semitones % 12));
      // Match score: fraction of expected intervals that are present.
      let matched = 0;
      for (const semi of expectedSemis) {
        if (intervalsFromRoot.has(semi)) matched++;
      }
      // Penalize extras (intervals present that aren't in pattern).
      const extras = intervalsFromRoot.size - matched;
      const score = matched - extras * 0.3;
      // Require ALL chord tones to be present for a valid match.
      if (matched === expectedSemis.size && (!bestMatch || score > bestMatch.score)) {
        bestMatch = { root: candidateRoot, quality, score };
      }
    }
  }

  if (!bestMatch) return null;
  return { root: bestMatch.root, quality: bestMatch.quality };
}

/** Given a chord, return a human-readable description of its notes and intervals. */
export function describeChord(c: Chord): string {
  const intervals = c.intervals.slice(1).map(intervalToString).join(", ");
  return `${c.symbol}: root ${pitchToString(c.root)}, intervals from root: ${intervals}, inversion ${c.inversion}`;
}

export const ALL_CHORD_QUALITIES: ChordQuality[] = Object.keys(CHORD_PATTERNS) as ChordQuality[];

/* ─── Slash chords ────────────────────────────────────────────────────────── */

/**
 * A chord with an explicit bass note. When the bass IS one of the chord
 * tones, this is functionally an inversion (e.g. C/E = C major in first
 * inversion). When the bass is a NON-chord-tone, it's a true slash chord
 * (e.g. F/G = F major triad over G bass — the G is not part of F major
 * and gives the chord a sus/dom-7-like color).
 *
 * The structure preserves the upper chord untouched and treats the bass
 * as a separate field, which makes both cases — inversion vs. true
 * slash — easy to tell apart via {@link bassIsChordTone}.
 */
export interface SlashChord {
  /** The chord written above the slash (root position; ignore inversion field). */
  upper: Chord;
  /** The bass note — sounds below the upper chord. */
  bass: Pitch;
  /** Display symbol, e.g. "F/G" or "C/E". */
  symbol: string;
}

/** Build a slash chord from an upper chord and a bass pitch. */
export function slashChord(upper: Chord, bass: Pitch): SlashChord {
  const bassName = pitchToString(bass).replace(/-?\d+$/, "");
  return {
    upper,
    bass,
    symbol: `${upper.symbol}/${bassName}`,
  };
}

/**
 * Voicing for a slash chord: bass note first (lowest), then the upper
 * chord's pitches in close root position above. The upper chord stays
 * in its written register — callers control absolute octaves via the
 * input pitches.
 */
export function slashChordVoicing(s: SlashChord): Pitch[] {
  return [s.bass, ...chordPitches(s.upper)];
}

/**
 * True when the bass is one of the upper chord's tones (i.e. the slash
 * chord is functionally an inversion). False when the bass is a
 * non-chord-tone — the case the curriculum's `slash-chords` node
 * specifically drills.
 */
export function bassIsChordTone(s: SlashChord): boolean {
  const upperPCs = new Set(chordPitches(s.upper).map(pitchClass));
  return upperPCs.has(pitchClass(s.bass));
}
